import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('winluxor_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const registerUser = (data) => api.post('/api/auth/register', data);
export const loginUser = (data) => api.post('/api/auth/login', data);
export const adminLogin = (data) => api.post('/api/auth/admin-login', data);
export const getMe = () => api.get('/api/auth/me');
export const updateProfile = (data) => api.put('/api/auth/profile', data);
export const changePassword = (data) => api.put('/api/auth/password', data);

// Raffles
export const getRaffles = (params) => api.get('/api/raffles', { params });
export const getRaffle = (id) => api.get(`/api/raffles/${id}`);

// Entries
export const createEntry = (data) => api.post('/api/entries', data);
export const getMyEntries = () => api.get('/api/entries/my');

// Packs
export const getPacks = () => api.get('/api/packs');

// Transactions
export const getTransactions = () => api.get('/api/transactions');

// Winners
export const getWinners = () => api.get('/api/winners');

// Completed Draws / History
export const getCompletedDraws = () => api.get('/api/draws/completed');

// Referral
export const applyReferral = (data) => api.post('/api/referral/apply', data);

// Settings
export const getSettings = () => api.get('/api/settings');

// Admin
export const getAdminDashboard = () => api.get('/api/admin/dashboard');
export const getAdminRaffles = () => api.get('/api/admin/raffles');
export const createAdminRaffle = (data) => api.post('/api/admin/raffles', data);
export const updateAdminRaffle = (id, data) => api.put(`/api/admin/raffles/${id}`, data);
export const deleteAdminRaffle = (id) => api.delete(`/api/admin/raffles/${id}`);
export const drawWinner = (id) => api.post(`/api/admin/raffles/${id}/draw`);
export const resendRaffleLaunchEmail = (id) => api.post(`/api/admin/raffles/${id}/resend-launch-email`);
export const sendRaffleTicketsLeftEmail = (id) => api.post(`/api/admin/raffles/${id}/send-tickets-left-email`);
export const resendRaffleSoldOutEmail = (id) => api.post(`/api/admin/raffles/${id}/resend-sold-out-email`);
export const resendRaffleDrawDateEmail = (id) => api.post(`/api/admin/raffles/${id}/resend-draw-date-email`);
export const previewRaffleEmail = (id, emailType, audience = 'default', ticketsOverride = null) =>
  api.get(`/api/admin/raffles/${id}/preview-email`, { params: { email_type: emailType, audience, ...(ticketsOverride ? { tickets_override: ticketsOverride } : {}) } });
export const resendRaffleLaunchEmailAud = (id, audience) =>
  api.post(`/api/admin/raffles/${id}/resend-launch-email`, null, { params: { audience } });
export const sendRaffleTicketsLeftEmailAud = (id, audience, ticketsOverride = null) =>
  api.post(`/api/admin/raffles/${id}/send-tickets-left-email`, null, { params: { audience, ...(ticketsOverride ? { tickets_override: ticketsOverride } : {}) } });
export const resendRaffleSoldOutEmailAud = (id, audience) =>
  api.post(`/api/admin/raffles/${id}/resend-sold-out-email`, null, { params: { audience } });
export const resendRaffleDrawDateEmailAud = (id, audience) =>
  api.post(`/api/admin/raffles/${id}/resend-draw-date-email`, null, { params: { audience } });
export const getAdminUsers = () => api.get('/api/admin/users');
export const creditPoints = (data) => api.post('/api/admin/users/credit', data);
export const blockUser = (userId) => api.put(`/api/admin/users/${userId}/block`);
export const unblockUser = (userId) => api.put(`/api/admin/users/${userId}/unblock`);
export const deleteUser = (userId) => api.delete(`/api/admin/users/${userId}`);
export const getAdminPacks = () => api.get('/api/admin/packs');
export const createAdminPack = (data) => api.post('/api/admin/packs', data);
export const updateAdminPack = (id, data) => api.put(`/api/admin/packs/${id}`, data);
export const deleteAdminPack = (id) => api.delete(`/api/admin/packs/${id}`);
export const getAdminPromos = () => api.get('/api/admin/promos');
export const createAdminPromo = (data) => api.post('/api/admin/promos', data);
export const updateAdminPromo = (id, data) => api.put(`/api/admin/promos/${id}`, data);
export const deleteAdminPromo = (id) => api.delete(`/api/admin/promos/${id}`);
export const validatePromo = (data) => api.post('/api/promo/validate', data);

// Notifications
export const getNotifications = () => api.get('/api/notifications');
export const markNotificationRead = (id) => api.put(`/api/notifications/${id}/read`);
export const getUnreadCount = () => api.get('/api/notifications/unread-count');

// My Wins
export const getMyWins = () => api.get('/api/wins/my');

// CMS
export const getCmsContent = (section) => api.get(`/api/cms/${section}`);
export const updateCmsContent = (section, data) => api.put(`/api/admin/cms/${section}`, data);
export const getAdminCms = () => api.get('/api/admin/cms');

// Admin Notifications
export const sendAdminNotification = (data) => api.post('/api/admin/notifications', data);

// Spin Wheel
export const dailySpin = () => api.post('/api/spin');
export const getSpinStatus = () => api.get('/api/spin/status');
export const getSpinConfig = () => api.get('/api/spin/config');

// Admin Spin
export const getAdminSpin = () => api.get('/api/admin/spin');
export const updateAdminSpin = (data) => api.put('/api/admin/spin', data);

// Subscriptions
export const getSubscriptionPlans = () => api.get('/api/subscriptions/plans');
export const getMySubscription = () => api.get('/api/subscriptions/my');
export const cancelMySubscription = () => api.post('/api/subscriptions/cancel');

// Chat
export const sendChat = (data) => api.post('/api/chat/send', data);
export const getChatMessages = () => api.get('/api/chat/messages');

// Social Share
export const trackShare = (data) => api.post('/api/share/track', data);

// Banners
export const getBanners = () => api.get('/api/banners');
export const getAdminBanners = () => api.get('/api/admin/banners');
export const createAdminBanner = (data) => api.post('/api/admin/banners', data);
export const updateAdminBanner = (id, data) => api.put(`/api/admin/banners/${id}`, data);
export const deleteAdminBanner = (id) => api.delete(`/api/admin/banners/${id}`);

// Admin Chat
export const getAdminChatConversations = () => api.get('/api/admin/chat/conversations');
export const getAdminChatMessages = (userId) => api.get(`/api/admin/chat/${userId}`);
export const adminChatReply = (userId, data) => api.post(`/api/admin/chat/${userId}/reply`, data);

// Admin Analytics
export const getAdminAnalytics = () => api.get('/api/admin/analytics');

// Admin VIP Plans
export const getAdminVipPlans = () => api.get('/api/admin/vip-plans');
export const updateAdminVipPlans = (data) => api.put('/api/admin/vip-plans', data);

// Admin Export
export const exportCSV = (type) => api.get(`/api/admin/export/${type}`, { responseType: 'blob' });

// Admin Newsletter
export const getAdminNewsletters = () => api.get('/api/admin/newsletters');
export const createAdminNewsletter = (data) => api.post('/api/admin/newsletters', data);
export const sendAdminNewsletter = (id) => api.post(`/api/admin/newsletters/${id}/send`);
export const deleteAdminNewsletter = (id) => api.delete(`/api/admin/newsletters/${id}`);
export const getAdminEmailSettings = () => api.get('/api/admin/email-settings');
export const updateAdminEmailSettings = (data) => api.put('/api/admin/email-settings', data);
export const getAdminEmailQueue = () => api.get('/api/admin/email-queue');
export const updateSettings = (data) => api.put('/api/admin/settings', data);
export const updateAdminPassword = (data) => api.put('/api/admin/password', data);

// Admin Winners (2-stage workflow)
export const getAdminRaffleEntries = (raffleId) => api.get(`/api/admin/raffles/${raffleId}/entries`);
export const exportRaffleTickets = (raffleId) => api.get(`/api/admin/raffles/${raffleId}/tickets-export`, { responseType: 'blob' });
export const closeRaffle = (raffleId) => api.put(`/api/admin/raffles/${raffleId}/close`);
export const getEntryTickets = (entryId) => api.get(`/api/entries/${entryId}/tickets`);
export const submitTestimonial = (winnerId, text) => api.put(`/api/winners/me/${winnerId}/testimonial`, { testimonial: text });
export const validateTestimonial = (winnerId, action, edited) => api.put(`/api/admin/winners/${winnerId}/testimonial-validate`, { action, testimonial: edited });
export const backfillTickets = (raffleId) => api.post(`/api/admin/raffles/${raffleId}/backfill-tickets`);

// Package templates
export const getPackageTemplates = () => api.get('/api/admin/package-templates');
export const createPackageTemplate = (name, packages) => api.post('/api/admin/package-templates', { name, packages });
export const updatePackageTemplate = (id, data) => api.put(`/api/admin/package-templates/${id}`, data);
export const deletePackageTemplate = (id) => api.delete(`/api/admin/package-templates/${id}`);
export const designateWinner = (raffleId, userId, winningTicket, winningCode) => api.post(`/api/admin/raffles/${raffleId}/designate-winner`, { user_id: userId, winning_ticket_number: winningTicket, winning_ticket_code: winningCode });
export const getAdminWinners = (status) => api.get('/api/admin/winners', { params: status ? { status } : {} });
export const publishWinner = (winnerId, data) => api.put(`/api/admin/winners/${winnerId}/publish`, data);
export const createManualWinner = (data) => api.post('/api/admin/winners/manual', data);
export const updateAdminWinner = (winnerId, data) => api.put(`/api/admin/winners/${winnerId}`, data);
export const unpublishWinner = (winnerId) => api.put(`/api/admin/winners/${winnerId}/unpublish`);
export const deleteAdminWinner = (winnerId) => api.delete(`/api/admin/winners/${winnerId}`);
export const setRaffleFeatured = (raffleId) => api.put(`/api/admin/raffles/${raffleId}/feature`);
export const unsetRaffleFeatured = (raffleId) => api.put(`/api/admin/raffles/${raffleId}/unfeature`);

// Notification celebrated
export const markNotificationCelebrated = (notifId) => api.put(`/api/notifications/${notifId}/celebrated`);

// File upload helper (admin only). Returns { url, filename }.
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/api/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Backup / Restore (admin only)
export const downloadDesignBackup = () =>
  api.get('/api/admin/backup/design', { responseType: 'blob' });

export const downloadClientBackup = () =>
  api.get('/api/admin/backup/clients', { responseType: 'blob' });

export const restoreBackup = (file, mode = 'replace', onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/api/admin/backup/restore?mode=${mode}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
    timeout: 600000, // 10 min for big backups
    maxContentLength: 1024 * 1024 * 1024,
    maxBodyLength: 1024 * 1024 * 1024,
  });
};

// ── CMS Legal Pages ──────────────────────────────────────────
export const getLegalPagesPublic = () => api.get('/api/legal-pages');
export const getLegalPagePublic = (slug) => api.get(`/api/legal-pages/${slug}`);
export const adminListLegalPages = () => api.get('/api/admin/legal-pages');
export const adminUpdateLegalPage = (slug, data) => api.put(`/api/admin/legal-pages/${slug}`, data);

// ── Inline text overrides ────────────────────────────────────
export const getTextOverrides = () => api.get('/api/text-overrides');
export const adminListTextOverrides = () => api.get('/api/admin/text-overrides');
export const adminUpsertTextOverride = (data) => api.put('/api/admin/text-overrides', data);
export const adminDeleteTextOverride = (key) => api.delete(`/api/admin/text-overrides/${key}`);

// ── AI Translation (admin only) ──────────────────────────────
export const adminTranslate = (text, source = 'fr', target = 'en', context = null) =>
  api.post('/api/admin/translate', { text, source, target, context });

// ── Stripe Payments ──────────────────────────────────────────
export const getStripePublicConfig = () => api.get('/api/stripe/public-config');
export const adminGetAirwallexConfig = () => api.get('/api/admin/airwallex/config');
export const adminUpdateAirwallexConfig = (data) => api.put('/api/admin/airwallex/config', data);
export const adminTestAirwallex = () => api.post('/api/admin/airwallex/test');
export const adminGetPaymentProvider = () => api.get('/api/admin/payments/provider');
export const adminSetPaymentProvider = (provider) => api.put('/api/admin/payments/provider', { provider });
export const adminGetStripeConfig = () => api.get('/api/admin/stripe/config');
export const adminUpdateStripeConfig = (data) => api.put('/api/admin/stripe/config', data);
export const adminTestStripe = () => api.post('/api/admin/stripe/test');
export const createCheckout = (data) => api.post('/api/checkout/create', data);
export const getCheckoutStatus = (sessionId) => api.get(`/api/checkout/status/${sessionId}`);
export const adminListPayments = () => api.get('/api/admin/payment-transactions');
export const adminGetSalesDashboard = (raffleId = null) => {
  const q = raffleId && raffleId !== 'all' ? `?raffle_id=${encodeURIComponent(raffleId)}` : '';
  return api.get(`/api/admin/sales/dashboard${q}`);
};
export const adminGetSalesAll = (params = {}) => {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.type_filter) q.set('type_filter', params.type_filter);
  if (params.user_email) q.set('user_email', params.user_email);
  if (params.raffle_id) q.set('raffle_id', params.raffle_id);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.skip) q.set('skip', String(params.skip));
  return api.get(`/api/admin/sales/all?${q.toString()}`);
};
export const adminGetSalesByUser = (userId) => api.get(`/api/admin/sales/by-user/${userId}`);
export const adminGetSalesByRaffle = () => api.get('/api/admin/sales/by-raffle');
export const adminResyncTransaction = (sessionId) => api.post(`/api/admin/sales/resync/${sessionId}`);
export const adminSyncAllPending = (hours = 24) => api.post(`/api/admin/sales/sync-all-pending?hours=${hours}`);
export const adminForceFulfilTransaction = (sessionId) => api.post(`/api/admin/payment-transactions/${sessionId}/force-fulfil`);
export const adminUpdateTransactionMethod = (sessionId, paymentMethod) =>
  api.put(`/api/admin/transactions/${sessionId}/method`, { payment_method: paymentMethod });
export const getMyPayments = () => api.get('/api/payment-transactions/my');

// ── NOWPayments (crypto gateway) ─────────────────────────────
export const adminGetNowPaymentsConfig = () => api.get('/api/admin/nowpayments/config');
export const adminUpdateNowPaymentsConfig = (data) => api.put('/api/admin/nowpayments/config', data);
export const adminTestNowPayments = () => api.post('/api/admin/nowpayments/test');
export const getNowPaymentsPublicConfig = () => api.get('/api/nowpayments/public-config');
export const getCryptoCurrencies = () => api.get('/api/nowpayments/currencies');
export const estimateCrypto = (amount, currencyTo, currencyFrom = 'gbp') =>
  api.get(`/api/nowpayments/estimate?amount=${amount}&currency_to=${currencyTo}&currency_from=${currencyFrom}`);
export const createCryptoCheckout = (data) => api.post('/api/checkout/crypto', data);
export const getCryptoCheckoutStatus = (paymentId) => api.get(`/api/checkout/crypto/status/${paymentId}`);

// ── Client errors (admin) ────────────────────────────────────
export const adminGetClientErrors = (limit = 100) => api.get(`/api/admin/client-errors?limit=${limit}`);
export const adminClearClientErrors = () => api.delete('/api/admin/client-errors');

// ── Visitor analytics ────────────────────────────────────────
export const visitorPing = (data) => api.post('/api/visitor/ping', data);
export const adminGetVisitorsLive = () => api.get('/api/admin/visitors/live');
export const adminGetVisitorsStats = () => api.get('/api/admin/visitors/stats');
export const adminGetVisitorsLiveCount = () => api.get('/api/admin/visitors/live-count');
export const adminGetUserVisitHistory = (userId) => api.get(`/api/admin/users/${userId}/visit-history`);

// ── Password reset ───────────────────────────────────────────
export const forgotPassword = (data) => api.post('/api/auth/forgot-password', data);
export const resetPassword = (data) => api.post('/api/auth/reset-password', data);
export const verifyResetToken = (token) => api.get(`/api/auth/reset-password/verify/${token}`);

// ── Express / Passwordless ───────────────────────────────────
export const expressStart = (data) => api.post('/api/express/start', data);
export const requestLoginCode = (data) => api.post('/api/auth/request-code', data);
export const verifyLoginCode = (data) => api.post('/api/auth/verify-code', data);
export const magicLogin = (token) => api.post('/api/auth/magic-login', { token });
export const googleConfig = () => api.get('/api/auth/google/config');
export const googleSession = (payload) => api.post('/api/auth/google/session', payload);
export const adminGetGoogleSettings = () => api.get('/api/admin/settings/google');
export const adminUpdateGoogleSettings = (data) => api.post('/api/admin/settings/google', data);
export const adminSendMagicLink = (userId) => api.post(`/api/admin/users/${userId}/send-magic-link`);
export const adminGetUserEntries = (userId) => api.get(`/api/admin/users/${userId}/entries`);
export const adminResendTicketsEmail = (userId, raffleId) => api.post(`/api/admin/users/${userId}/resend-tickets-email/${raffleId}`);

// ── WhatsApp contacts (admin) ────────────────────────────────
export const adminListWhatsAppPending = () => api.get('/api/admin/users/whatsapp-pending');
export const adminListWhatsAppHistory = () => api.get('/api/admin/users/whatsapp-history');
export const adminMarkWhatsAppContacted = (userId) => api.post(`/api/admin/users/${userId}/mark-whatsapp-contacted`);
export const adminUnmarkWhatsAppContacted = (userId) => api.post(`/api/admin/users/${userId}/unmark-whatsapp-contacted`);
export const adminSendTestEmail = (toEmail) => api.post('/api/admin/email/test', { to_email: toEmail });

export default api;
